export interface ProfilerPlanNode {
  label: string;
  time?: string;
  results?: string;
  cardinality?: number;
  memory?: string;
  spilled?: string;
  gaps?: string;
  children?: ProfilerPlanNode[];
  vars?: string;
  relNode?: string;
  query?: string;
}

export interface ProfilerJson {
  prefixes?: Record<string, string>;
  profiler?: {
    memory?: string;
    time: string;
    results?: string;
    error?: string;
    'pre-execution'?: string;
    'time unit': string;
  };
  dataset?: Partial<Record<DatasetScope, string[]>>;
  plan: ProfilerPlanNode;
}

/** Dataset clauses the server can report, keyed as they appear in the JSON. */
export type DatasetScope =
  | 'from'
  | 'from_named'
  | 'insert_into'
  | 'delete_from';

export interface ProfilerFormatResult {
  text: string;
  /**
   * Maps 1-indexed line numbers (as string keys) to the plan node label on
   * that line. Plain object rather than a Map so it stays serializable in
   * Redux state.
   */
  lineToLabel: Record<string, string>;
  /** Prefix mappings from the profiler response (name → namespace URI). */
  prefixes: Record<string, string>;
}

const formatCardinality = (cardinality: number): string => {
  if (cardinality >= 1_000_000) {
    const val = cardinality / 1_000_000;
    return Number.isInteger(val) ? `${val}M` : `${val.toFixed(1)}M`;
  }
  if (cardinality >= 1_000) {
    const val = cardinality / 1_000;
    return Number.isInteger(val) ? `${val}K` : `${val.toFixed(1)}K`;
  }
  return String(cardinality);
};

const formatNode = (
  node: ProfilerPlanNode,
  totalTime: number,
  depth: number,
  prefix: string,
  isLast: boolean,
  isRoot: boolean,
  lines: string[],
  lineToLabel: Record<string, string>
) => {
  let label = prefix;
  if (!isRoot) {
    label += `${isLast ? '`' : '+'}─ `;
  }

  label += node.label;

  // The cardinality marker (also reused verbatim on a Virtual Graph node's
  // closing `}` line, matching the CLI).
  const cardStr =
    node.cardinality !== undefined
      ? ` [#${formatCardinality(node.cardinality)}]`
      : '';
  label += cardStr;

  // Node metrics, in the same order the CLI renders them: memory (with any
  // spilled-to-disk amount), results (optionally flagged "with gaps"), then
  // wall time.
  if (node.memory !== undefined) {
    label += `, memory: ${node.memory}`;
  }
  if (node.spilled !== undefined) {
    label += `, spilled to disk: ${node.spilled}`;
  }
  if (node.results !== undefined) {
    label += `, results: ${node.results}`;
  }
  if (node.gaps === 'true') {
    label += ' with gaps';
  }
  if (totalTime > 0 && node.time !== undefined) {
    const time = parseInt(node.time, 10) || 0;
    label += `, wall time: ${node.time} ms (${(
      (time / totalTime) *
      100
    ).toFixed(1)}%)`;
  }

  // Virtual Graph nodes have an empty `children` array and stash their detail
  // in `relNode`/`query`/`vars`. The CLI wraps that detail in `{ … }` after the
  // node label. `nodeContinuation` is this node's base indent (where its detail
  // and closing brace sit, and the base for its children).
  const isVirtualGraph =
    node.relNode !== undefined ||
    node.query !== undefined ||
    node.vars !== undefined;
  if (isVirtualGraph) {
    label += ' {';
  }

  // A label can span multiple Monaco lines (e.g. a Projection with many
  // CONSTRUCT bindings, where `node.label` itself contains `\n`). Push each
  // Monaco line separately so `lines.length` always reflects the Monaco line
  // count, then map every spanned line to the same label so clicks on
  // continuation lines still resolve to the node. Continuation lines are
  // indented to align with the first line's content (past the tree connector)
  // so the tree structure stays readable.
  const labelLines = label.split('\n');
  const continuationIndent = isRoot
    ? ''
    : `${prefix}${isLast ? '   ' : '│  '}  `;
  labelLines.forEach((l, i) =>
    lines.push(i === 0 ? l : `${continuationIndent}${l}`)
  );
  // Monaco lines are 1-indexed.
  const startLine = lines.length - labelLines.length + 1;
  for (let l = startLine; l <= lines.length; l += 1) {
    lineToLabel[l] = node.label;
  }

  const nodeContinuation = isRoot ? '' : `${prefix}${isLast ? '   ' : '│  '}`;

  // Render the Virtual Graph detail sections (matching the CLI's
  // `RelNode=`/`Query=`/`Vars=` labels and order) followed by the closing brace.
  if (isVirtualGraph) {
    const contentPrefix = `${nodeContinuation}   `;
    const sections: Array<[string, string | undefined]> = [
      ['RelNode=', node.relNode],
      ['Query=', node.query],
      ['Vars=', node.vars],
    ];
    sections.forEach(([heading, block]) => {
      if (block === undefined) {
        return;
      }
      // Each field value is already indented relative to its heading.
      [heading, ...block.split('\n')].forEach((l) => {
        lines.push(`${contentPrefix}${l}`);
        lineToLabel[lines.length] = node.label;
      });
    });
    lines.push(`${nodeContinuation}}${cardStr}`);
    lineToLabel[lines.length] = node.label;
  }

  const children = node.children || [];
  const childCount = children.length;
  children.forEach((child, i) => {
    const childIsLast = i === childCount - 1;
    const childPrefix = nodeContinuation;
    formatNode(
      child,
      totalTime,
      depth + 1,
      childPrefix,
      childIsLast,
      false,
      lines,
      lineToLabel
    );
  });
};

/**
 * Formats a profiler JSON response into readable text for the Monaco editor.
 * Returns null if the input doesn't look like valid profiler JSON.
 */
export const formatProfilerJson = (
  json: unknown
): ProfilerFormatResult | null => {
  if (!json || typeof json !== 'object' || !('plan' in json)) {
    return null;
  }

  const data = json as ProfilerJson;
  const lines: string[] = [];
  const lineToLabel: Record<string, string> = {};

  // Header section
  if (data.profiler) {
    const { time, results, memory, error } = data.profiler;
    const preExecution = data.profiler['pre-execution'];
    const timeUnit = data.profiler['time unit'] || 'ms';
    lines.push('Profiling results:');
    if (results) {
      lines.push(
        `Query executed in ${time} ${timeUnit} and returned ${results} result(s)`
      );
    } else {
      lines.push(`Query executed in ${time} ${timeUnit}`);
    }
    if (error) {
      lines.push(error);
    }
    if (memory) {
      lines.push(`Total used memory: ${memory}`);
    }
    if (preExecution) {
      lines.push(`Pre-execution: ${preExecution} ${timeUnit}`);
    }
    lines.push('');
  }

  // Prefixes (kept in the response's declaration order to match the CLI).
  if (data.prefixes) {
    const entries = Object.entries(data.prefixes);
    if (entries.length > 0) {
      entries.forEach(([name, uri]) => {
        lines.push(`prefix ${name}: <${uri}>`);
      });
      lines.push('');
    }
  }

  // Dataset — one line per graph, in the CLI's keyword order. Named graphs are
  // wrapped in angle brackets unless they are the special `all`/`local` scopes.
  if (data.dataset) {
    const datasetSections: Array<[DatasetScope, string]> = [
      ['from', 'From'],
      ['from_named', 'From named'],
      ['insert_into', 'Insert into'],
      ['delete_from', 'Delete from'],
    ];
    datasetSections.forEach(([key, keyword]) => {
      const graphs = data.dataset?.[key];
      if (graphs) {
        graphs.forEach((graph) => {
          const isSpecial =
            graph === 'all' || graph === 'local' || graph === 'local named';
          lines.push(
            isSpecial ? `${keyword} ${graph}` : `${keyword} <${graph}>`
          );
        });
      }
    });
  }

  // Plan tree
  const totalTime = data.profiler ? parseInt(data.profiler.time, 10) || 0 : 0;
  formatNode(data.plan, totalTime, 0, '', true, true, lines, lineToLabel);

  return { text: lines.join('\n'), lineToLabel, prefixes: data.prefixes || {} };
};
